import { Dropdown } from '@silverfort/sf-components';
import { FC, useMemo, useState } from 'react';
import { SELECTOR_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import ADEntitiesSelector from '../../../../components/Controls/Modals/ADEntitiesSelector/ADEntitiesSelector';
import FilterDropdown from './FilterDropdown';
import { FilterConfig } from './types';
import { LogsFilters, Timeframe } from '../../types';

interface FiltersProps {
    filters: LogsFilters;
    onFiltersChange: (filters: LogsFilters) => void;
}

interface EntityItem {
    name?: string;
    displayName?: string;
    sam?: string;
    guid: string;
    address?: string;
    mask?: string;
    identifier?: string;
}

interface SelectedEntities {
    objectEntities: EntityItem[];
    ipEntities: EntityItem[];
    unmanagedEntities: EntityItem[];
    applicationEntities: EntityItem[];
    selectAllEntities: boolean;
}

const TIMEFRAMES = ['7', '14', '30', '90'];
const DEFAULT_TIMEFRAME = '30';

const DEVICES_ONLY = 'DEVICES_ONLY';
const SERVERS_ONLY = 'SERVERS_ONLY';

const SOURCE_FILTER_CONFIG: FilterConfig = {
    id: 'sources',
    label: 'Source',
    externalFilterType: DEVICES_ONLY
};

const DESTINATION_FILTER_CONFIG: FilterConfig = {
    id: 'destinations',
    label: 'Destination',
    externalFilterType: SERVERS_ONLY,
    allowApplications: true
};

const TIMEFRAME_ITEMS = [...TIMEFRAMES.map((t) => ({ value: t, title: `${t} days` }))];

const Filters: FC<FiltersProps> = ({ filters, onFiltersChange }) => {
    const [showSelectEntitiesModal, setShowSelectEntitiesModal] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterConfig | null>(null);
    const [selectedEntities, setSelectedEntities] = useState<SelectedEntities | null>(null);

    const handleEntitiesFilter = (filterConfig: FilterConfig) => {
        setActiveFilter(filterConfig);
        setShowSelectEntitiesModal(true);
    };

    const handleCloseModal = () => {
        setShowSelectEntitiesModal(false);
        setActiveFilter(null);
    };

    const selectEntitiesHandler = (selectedEntities: SelectedEntities) => {
        if (!activeFilter) return;

        const updatedFilters = { ...filters };

        if (activeFilter.id === 'sources') {
            updatedFilters.includeSources = selectedEntities.objectEntities.map((entity) => entity.guid);
            updatedFilters.includeSourcesIps = selectedEntities.ipEntities
                .filter((entity) => entity.address)
                .map((entity) => entity.address!);
            updatedFilters.includeSourceIpOrName = selectedEntities.unmanagedEntities
                .filter((entity) => entity.name)
                .map((entity) => entity.name!);
        } else if (activeFilter.id === 'destinations') {
            updatedFilters.includeDestinations = selectedEntities.objectEntities.map((entity) => entity.guid);
            updatedFilters.includeDestinationsIps = selectedEntities.ipEntities
                .filter((entity) => entity.address)
                .map((entity) => entity.address!);
            updatedFilters.includeDestinationsIpOrName = selectedEntities.unmanagedEntities
                .filter((entity) => entity.name)
                .map((entity) => entity.name!);
            updatedFilters.includeDestinationsApplications = selectedEntities.applicationEntities.map(
                (entity) => entity.guid
            );
        }
        onFiltersChange(updatedFilters);
        handleCloseModal();
        setSelectedEntities(selectedEntities);
    };

    const getSelectedEntitiesForModal = (filterType: 'sources' | 'destinations') => {
        const entities: { guid: string; name?: string; displayName?: string }[] = [];

        if (filterType === 'sources') {
            if (filters.includeSources) {
                filters.includeSources.forEach((guid) => {
                    entities.push({
                        guid,
                        name: selectedEntities?.objectEntities.find((entity) => entity.guid === guid)?.name,
                        displayName: selectedEntities?.objectEntities.find((entity) => entity.guid === guid)
                            ?.displayName
                    });
                });
            }
        } else {
            if (filters.includeDestinations) {
                filters.includeDestinations.forEach((guid) => {
                    entities.push({
                        guid,
                        name: selectedEntities?.objectEntities.find((entity) => entity.guid === guid)?.name,
                        displayName: selectedEntities?.objectEntities.find((entity) => entity.guid === guid)
                            ?.displayName
                    });
                });
            }
        }

        return entities;
    };

    const getSelectedIpEntitiesForModal = (filterType: 'sources' | 'destinations') => {
        const ipEntities: { address: string; mask: string; identifier: string }[] = [];

        if (filterType === 'sources' && filters.includeSourcesIps) {
            filters.includeSourcesIps.forEach((ip) => {
                const [address, mask] = ip.includes('/') ? ip.split('/') : [ip, ''];
                ipEntities.push({ address, mask, identifier: ip });
            });
        } else if (filterType === 'destinations' && filters.includeDestinationsIps) {
            filters.includeDestinationsIps.forEach((ip) => {
                const [address, mask] = ip.includes('/') ? ip.split('/') : [ip, ''];
                ipEntities.push({ address, mask, identifier: ip });
            });
        }

        return ipEntities;
    };

    const getSelectedUnmanagedEntitiesForModal = (filterType: 'sources' | 'destinations') => {
        const unmanagedEntities: { name: string; unmanaged: true }[] = [];

        if (filterType === 'sources' && filters.includeSourceIpOrName) {
            filters.includeSourceIpOrName.forEach((name) => {
                unmanagedEntities.push({ name, unmanaged: true });
            });
        } else if (filterType === 'destinations' && filters.includeDestinationsIpOrName) {
            filters.includeDestinationsIpOrName.forEach((name) => {
                unmanagedEntities.push({ name, unmanaged: true });
            });
        }

        return unmanagedEntities;
    };

    const getSelectedApplicationEntitiesForModal = (filterType: 'sources' | 'destinations') => {
        const applicationEntities: { guid: string; name?: string; displayName?: string; applicationEntity: true }[] =
            [];

        if (filterType === 'destinations' && filters.includeDestinationsApplications) {
            filters.includeDestinationsApplications.forEach((guid) => {
                applicationEntities.push({
                    guid,
                    name: selectedEntities?.applicationEntities.find((entity) => entity.guid === guid)?.name,
                    displayName: selectedEntities?.applicationEntities.find((entity) => entity.guid === guid)
                        ?.displayName,
                    applicationEntity: true
                });
            });
        }

        return applicationEntities;
    };

    return (
        <>
            <div className="flex flex-row gap-2">
                <Dropdown
                    className={
                        filters.timeframe === DEFAULT_TIMEFRAME
                            ? 'bg-sf-white [&]:border-sf-gray-100 [&:hover]:border-sf-gray-400 active:border-sf-gray-800'
                            : ''
                    }
                    variant="filter"
                    multiple={false}
                    searchable={false}
                    title="Timeframe"
                    removable={filters.timeframe !== DEFAULT_TIMEFRAME}
                    selectedItems={filters.timeframe}
                    items={TIMEFRAME_ITEMS}
                    onChange={(selectedValues) => {
                        onFiltersChange({ ...filters, timeframe: selectedValues as Timeframe });
                    }}
                    onRemove={() => onFiltersChange({ ...filters, timeframe: DEFAULT_TIMEFRAME })}
                    width="232px"
                />

                <FilterDropdown
                    filterType="sources"
                    filterConfig={SOURCE_FILTER_CONFIG}
                    filters={filters}
                    onFilterClick={handleEntitiesFilter}
                    onRemoveFilter={() =>
                        onFiltersChange({
                            ...filters,
                            includeSources: [],
                            includeSourcesIps: [],
                            includeSourceIpOrName: []
                        })
                    }
                />

                <FilterDropdown
                    filterType="destinations"
                    filterConfig={DESTINATION_FILTER_CONFIG}
                    filters={filters}
                    onFilterClick={handleEntitiesFilter}
                    onRemoveFilter={() =>
                        onFiltersChange({
                            ...filters,
                            includeDestinations: [],
                            includeDestinationsIps: [],
                            includeDestinationsIpOrName: [],
                            includeDestinationsApplications: []
                        })
                    }
                />
            </div>

            {showSelectEntitiesModal && activeFilter && (
                <ADEntitiesSelector
                    selectedObjectEntities={getSelectedEntitiesForModal(activeFilter.id as 'sources' | 'destinations')}
                    selectedUnmanagedEntities={getSelectedUnmanagedEntitiesForModal(
                        activeFilter.id as 'sources' | 'destinations'
                    )}
                    selectedIpEntities={getSelectedIpEntitiesForModal(activeFilter.id as 'sources' | 'destinations')}
                    selectedApplicationEntities={getSelectedApplicationEntitiesForModal(
                        activeFilter.id as 'sources' | 'destinations'
                    )}
                    selectorType={SELECTOR_TYPES[activeFilter.externalFilterType]}
                    modalTitle={`Select ${activeFilter.label}`}
                    showModal={showSelectEntitiesModal}
                    handleCloseModal={handleCloseModal}
                    saveEntities={selectEntitiesHandler}
                    showExcluded={false}
                    disableAdd={false}
                    footerMsg=""
                    allowIps={activeFilter.externalFilterType === DEVICES_ONLY}
                    allowApplications={activeFilter.allowApplications || false}
                    disableNetMasks={false}
                    allowUnmanagedEntities={true}
                    removeEntitiesBlocked={false}
                    isEditor={true}
                    showSelectAll={false}
                    allowSelectAll={false}
                    selectAllText=""
                    allEntitiesSelected={false}
                    maxEntitySelection={undefined}
                    maxFileSize={undefined}
                    maxFileSizeText=""
                    authTypes={undefined}
                    footerComponent={undefined}
                    ipOnly={false}
                    disableAutoComplete={false}
                />
            )}
        </>
    );
};

export default Filters;
